import React, { useEffect } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import {
  DeferredCompensationContributionUnit,
  DeferredCompensationDistributionMethodDTO,
  DeferredCompensationDTO,
} from '../../tables/DeferredCompensation/DeferredCompensation.meta';
import { useDeferredCompensationForm } from './useDeferredCompensationForm';
import {
  DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
  DeferredCompensationFormValues,
} from './DeferredCompensationForm.meta';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { CustomDistributionSchedule } from './components/CustomDistributionSchedule';
import { APPEND_ASSET_OWNER, OwnerModelFormDTO } from '../../OtherAssets.meta';
import { AssetOwnership } from '../components/AssetOwnership/AssetOwnership';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { Checkbox } from '../../../../CommonComponents/OPL/Checkbox/Checkbox';

export interface Props {
  clientId: string;
  asset: DeferredCompensationDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export const DeferredCompensationForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
  onDirtyChange,
}: Props) => {
  const {
    data: {
      isDirty,
      control,
      errors,
      contributionUnit,
      defaultContributionUnitOption,
      contributionStartAgeOptions,
      distributionStartAgeOptions,
      contributionEndYearOptions,
      distributionPeriodOptions,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      watchedManagementStyleSelection,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
      distributionMethod,
    },
    actions: { onSubmit, register, setValue, resetValues },
  } = useDeferredCompensationForm({ clientId, options, asset, onSuccess });

  const { fields, update, append, remove } = useFieldArray<
    DeferredCompensationFormValues,
    'ownerModel',
    'fieldKey'
  >({
    control,
    name: 'ownerModel',
    keyName: 'fieldKey',
  });

  const watchOwner = useWatch({ control, name: 'ownerModel' });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    resetValues();
  }, [asset]);

  const updateOwner = (index: number, data: OwnerModelFormDTO) => {
    update(index, {
      ...ownerModelFields[index],
      ...data,
    });
  };

  const setPercentage = (percentArr: number[]) => {
    ownerModelFields.map((field, index) => {
      setValue(`ownerModel.${index}.Percentage`, percentArr[index].toString());
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="deferred compensation form"
    >
      <Input
        label="Description"
        formVariant={errors.name && 'error'}
        helperText={errors.name?.message}
        {...register('name')}
      />

      <AssetOwnership<DeferredCompensationFormValues>
        control={control}
        options={options.DeferredCompensationOptions.earner}
        allowsUnequalOwnership={options.DeferredCompensationOptions.AllowUnequalOwnership}
        ownerModelFields={ownerModelFields}
        updateOwner={updateOwner}
        appendOwner={() => append({ ...APPEND_ASSET_OWNER })}
        removeOwner={(index: number) => remove(index)}
        setPercentage={setPercentage}
        label="Earner"
      />

      <Controller
        control={control}
        name="value"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            decimalScale={2}
            allowNegative={false}
            label="Current Value"
            leftIcon="far fa-dollar-sign"
            formVariant={errors.value && 'error'}
            helperText={errors.value?.message}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="yearlyContribution"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            decimalScale={
              contributionUnit === DeferredCompensationContributionUnit.Dollar ? 2 : undefined
            }
            allowNegative={false}
            label="Annual Contribution"
            formVariant={errors.yearlyContribution && 'error'}
            helperText={errors.yearlyContribution?.message}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            selectorProps={{
              defaultValue: defaultContributionUnitOption,
              options: DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
              onChange: option => setValue('contributionUnit', option.value),
              ariaLabel: 'Annual contribution unit selector',
            }}
            {...field}
          />
        )}
      />

      <Box
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Controller
          name="contributionStartAge"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Contribution Start Age"
              formVariant={errors.contributionStartAge && 'error'}
              helperText={errors.contributionStartAge?.message}
              options={contributionStartAgeOptions}
              value={contributionStartAgeOptions.find(option => option.value === value)}
              onChange={option => onChange(option?.value)}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="contributionEndYear"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Contribution End Year"
              formVariant={errors.contributionEndYear && 'error'}
              helperText={errors.contributionEndYear?.message}
              options={contributionEndYearOptions}
              value={contributionEndYearOptions.find(option => option.value === value)}
              onChange={option => {
                onChange(option?.value);

                // reset dependent fields
                setValue('distributionStartAge', null);
              }}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="distributionStartAge"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Distribution Start Age"
              formVariant={errors.distributionStartAge && 'error'}
              helperText={errors.distributionStartAge?.message}
              options={distributionStartAgeOptions}
              value={distributionStartAgeOptions.find(option => option.value === value) || null}
              onChange={option => {
                onChange(option?.value);

                // reset dependent fields
                setValue('distributionPeriod', 1);
              }}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="distributionPeriod"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Distribution Years"
              formVariant={errors.distributionPeriod && 'error'}
              helperText={errors.distributionPeriod?.message}
              options={distributionPeriodOptions}
              value={distributionPeriodOptions.find(option => option.value === value)}
              onChange={option => onChange(option?.value)}
              innerRef={ref}
            />
          )}
        />
      </Box>

      <Box>
        <Controller
          control={control}
          name="distributionMethod"
          render={({ field: { value } }) => {
            return (
              <>
                <Radio
                  checked={value === DeferredCompensationDistributionMethodDTO.Auto}
                  onChange={() => {
                    setValue('distributionMethod', DeferredCompensationDistributionMethodDTO.Auto);
                  }}
                  mr="16"
                >
                  Automatically calculate distributions
                </Radio>

                <Radio
                  checked={value === DeferredCompensationDistributionMethodDTO.Custom}
                  onChange={() => {
                    setValue(
                      'distributionMethod',
                      DeferredCompensationDistributionMethodDTO.Custom,
                    );

                    // Initialize with empty schedule - the CustomDistributionSchedule component
                    // will sync it with distributionPeriod
                    setValue('customDistributionSchedule', []);
                  }}
                >
                  Use a custom distribution schedule
                </Radio>
              </>
            );
          }}
        />
      </Box>

      {distributionMethod === DeferredCompensationDistributionMethodDTO.Custom && (
        <CustomDistributionSchedule control={control} errors={errors} />
      )}

      <Box
        css={`
          display: grid;
          grid-template-columns: ${distributionMethod ===
          DeferredCompensationDistributionMethodDTO.Custom
            ? '1fr'
            : '1fr 1fr'};
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Controller
          control={control}
          name="accumulationGrowthRate"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              allowNegative={false}
              label="Accumulation Growth Rate"
              rightIcon="far fa-percent"
              formVariant={errors.accumulationGrowthRate && 'error'}
              helperText={errors.accumulationGrowthRate?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />

        {distributionMethod === DeferredCompensationDistributionMethodDTO.Auto && (
          <Controller
            control={control}
            name="distributionGrowthRate"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                allowNegative={false}
                label="Distribution Growth Rate"
                rightIcon="far fa-percent"
                formVariant={errors.distributionGrowthRate && 'error'}
                helperText={errors.distributionGrowthRate?.message}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />
        )}
      </Box>

      {assetBucketingByManagementStyle && (
        <ManagementStyle<DeferredCompensationFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<DeferredCompensationFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && watchedManagementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {isEstatePlanningWorkflow && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}
      <Box>
        <Text as="label" variant="formLabel" color="neutral.600">
          Net Worth Reporting
        </Text>
        <HelperText mb="8">
          Check the box to include the deferred compensation plan in current net worth values.
        </HelperText>
        <Controller
          control={control}
          name="excludeFromNetWorth"
          render={({ field: { value, name, onChange } }) => (
            <Checkbox
              name={name}
              checked={!value}
              onChange={evt => onChange(!evt.target.checked)}
              mr="4"
            >
              Include Deferred Compensation in Net Worth Reports
            </Checkbox>
          )}
        />
      </Box>
    </form>
  );
};
